import './FirstMarketplaceForm.scss';
import { useLazyQuery, useMutation } from '@apollo/client';
import Button from 'components/Button/Button';
import InputField from 'components/InputField/InputField';
import {
  checkMarketplaceConfigQuery,
  CreateMarketplaceInput,
  createMarketplaceMutation,
  UpdateMarketplaceInput,
  updateMarketplaceMutation,
  getMiraklPlatformConfig,
} from 'pages/FirstMarketplace/mutations';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { AdyenRoutesName } from 'routes/route-names';
import { getRoutePath } from 'routes/route-utils';
import {
  AccountConfig,
  AdyenNotificationSecurity,
  Channel,
  ChannelConfig,
  ChannelUpdateConfig,
  Marketplace,
  MarketplaceConfig,
  MiraklConfig,
  SettingTypes,
  UpdatedValues,
} from "TypesAndInterfaces";
import {
  displayError,
  displaySuccess,
  displayWarning,
} from "utils/displayNotification";
import { EMAIL_FORMAT, URL_FORMAT } from "utils/validation";
import { useCallback, useContext, useEffect, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import Modal from "components/Modal/Modal";
import AccountSettings from './AccountSettings';
import MiraklSettings from './MiraklSettings';
import ChannelSettings from './ChannelSettings';
import { FirstMarketplaceContext, UpdatesValues } from './FirstMarketplace.context';
import HealthStateIndicator from '../../pages/HealthStateIndicator/components/HealthStateIndicator';

interface IProps {
  marketplaceConfig: MarketplaceConfig;
  marketplaceId?: number;
  marketplaceName: string;
  emailList: string;
  disabledCreatedMarketplace: boolean;
  setMarketplaceConfig: Function;
  setPageTitle: Function;
  setShallowConfig: Function;
  setMarketplaceName: Function;
  setEmailList: Function;
  channels: Channel[];
}


interface MiraklPlatformConfigurations {
  features: {
    kycStatusPerSeller: boolean;
    paymentConfirmation: boolean;
    shopTaxOptions: {
      localTaxNumber: {
        mandatory: boolean;
      }
      taxIdentificationNumber: {
        mandatory: boolean;
      }
    };
  };
};




const FirstMarketplaceForm = ({
  marketplaceConfig,
  marketplaceId,
  marketplaceName,
  emailList,
  disabledCreatedMarketplace,
  setMarketplaceConfig,
  setPageTitle,
  setShallowConfig,
  setMarketplaceName,
  setEmailList,
  channels,
}: IProps) => {
  //#region hooks
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
  const [validateButtonDisabled, setValidateButtonDisabled] = useState(false);
  const [copyChannels, setCopyChannels] = useState<ChannelConfig[]>([]);
  const [isChannelModalOpened, setIsChannelModalOpened] = useState(false);
  const [isBalancePlatformModalOpened, setIsBalancePlatformModalOpened] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [balancePlatformName, setBalancePlatformName] = useState('');
  const [useChannels, setUseChannels] = useState(false);
  const [healthIndicatorState, setHealthIndicatorState] = useState<{ status: 0 | 1 | 2; messages: string[] }>({ status: 2, messages: [] });
  const [useBalancePlatforms, setUseBalancePlatforms] = useState(false);
  const { newValues, setNewValues, shopBlacklist, invoiceBlacklist } = useContext(FirstMarketplaceContext);
  let updatedValues: UpdatedValues = {}

  //#endregion

  //#region data
  const navigate = useNavigate();
  //#endregion

  useEffect(() => {
    if (channels.length) {
      // map channels into ChannelConfig by removing isDefault and marketplaceId fields
      const updated: ChannelConfig[] = channels.map((channel) => {
        const { marketplaceId, ...rest } = channel;
        return rest;
      });
      // set channels to copyChannels
      setCopyChannels(updated);
    } else {
      generateDefaultChannel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels]);

  useEffect(() => {
    if (marketplaceConfig.account.useChannels) {
      setUseChannels(marketplaceConfig.account.useChannels);
    }
    if (marketplaceConfig.account.useBalancePlatforms) {
      setUseBalancePlatforms(marketplaceConfig.account.useBalancePlatforms);
    }
  }, [marketplaceConfig]);


  //#region API CALLS
  const [createMarketplace] = useMutation<{ createMarketplace: Marketplace }, { data: CreateMarketplaceInput }>(createMarketplaceMutation, {
    onCompleted: (dataCreate) => {
      setSaveButtonDisabled(false);
      displaySuccess(`${dataCreate.createMarketplace.name} is created.`);
      setTimeout(() => {
        navigate({
          pathname: getRoutePath(AdyenRoutesName.EDIT_MARKETPLACE),
          search: `${createSearchParams({
            marketplaceId: dataCreate.createMarketplace.id.toString(),
          })}`,
        });
      }, 1000);
    },
    onError: (e) => {
      displayError(`Something went wrong ${e.message}`);
      setTimeout(() => {
        setSaveButtonDisabled(false);
      }, 1500);
    },
  });

  const [updateMarketplace] = useMutation<{ updateMarketplace: Marketplace }, { data: UpdateMarketplaceInput }>(updateMarketplaceMutation, {
    onCompleted: (data) => {
      const { config, name } = data.updateMarketplace;
      updatedValues = {}
      setSaveButtonDisabled(false);
      setNewValues([])
      setMarketplaceConfig(config);
      setPageTitle(`Update ${name}`);
      setEmailList(joinEmails(config.account.marketplaceOperatorEmails));
      setShallowConfig({
        invoiceSyncScheduler: config.account.invoiceSyncScheduler,
        accountFullSyncScheduler: config.account.accountFullSyncScheduler,
        accountPartialSyncScheduler: config.account.accountPartialSyncScheduler,
      });
      displaySuccess(`${name} is updated.`);
      const newChannels = data.updateMarketplace.channels
        .map((channel) => {
          const { marketplaceId, ...rest } = channel;
          return rest;
        }).sort((a, b) => a.code.localeCompare(b.code));
      setCopyChannels([...newChannels])
    },
    onError: (e) => {
      displayError(`Something went wrong ${e.message}`);
      setTimeout(() => {
        setSaveButtonDisabled(false);
      }, 1500);
    },
  });

  const [checkMarketplaceConfig] = useLazyQuery<
    {
      checkMarketplaceConfig: {
        checkMarketplaceConfig: string;
        miraklPlatformConfigurations: string;
      }
    },
    {
      config: MarketplaceConfig;
      channels: ChannelConfig[];
      marketplaceId: number;
    }>
    (checkMarketplaceConfigQuery, {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const result = data.checkMarketplaceConfig;
        const makeReadable = result.checkMarketplaceConfig;
        const miraklPlatformConfigurations = result.miraklPlatformConfigurations
          ? JSON.parse(result.miraklPlatformConfigurations)
          : undefined;

        updateMarketplaceValues();
        setValidateButtonDisabled(false);
        if (makeReadable) {
          displayWarning(makeReadable);
        } else {
          displaySuccess('Inputs are validated successfully.');
        }

        setHealthIndicatorState(
          manageHealthStateIndicator(
            miraklPlatformConfigurations ?? { features: { kycStatusPerSeller: false, paymentConfirmation: false, shopTaxOptions: { localTaxNumber: { mandatory: false }, taxIdentificationNumber: { mandatory: false } } } }
          )
        );
      },
      onError: (e) => {
        displayError(`Something went wrong ${e.message}`);
        setTimeout(() => {
          setValidateButtonDisabled(false);
        }, 1500);
      },
    });

  const [fetchHealthState] = useLazyQuery(getMiraklPlatformConfig, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.getMiraklPlatformConfig) {
        setHealthIndicatorState(
          manageHealthStateIndicator(data.getMiraklPlatformConfig)
        );
      }
    },
    onError: (e) => {
      setHealthIndicatorState({
        status: 0,
        messages: ["Could not fetch the health state of the marketplace."],
      });
    },
  });

  const fetchHealthStateCallback = useCallback(() => {
    if (marketplaceId) {
      fetchHealthState({
        variables: { marketplaceId: +marketplaceId },
      });
    }
  }, [fetchHealthState, marketplaceId]);

  useEffect(() => {
    fetchHealthStateCallback();
  }, [fetchHealthStateCallback]);

  const joinEmails = (list: string[]) => list.join(', ');

  const createEmails = () =>
    emailList
      .split(/[\s;,]+/)
      .map((i) => i.trim())
      .filter((email) => EMAIL_FORMAT.test(email));

  const handleStateUpdate = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>,
    number?: boolean,
    type?: SettingTypes,
    channelID?: number,
    toogleProperty?: string,
    toogleState?: boolean,
    routePrioritiesName?: string,
    routePriorities?: string[],
    marketplaceOperatorEmails?: string
  ) => {
    let property;
    let value;
    if (toogleProperty && toogleState !== undefined) {
      property = toogleProperty;
      value = toogleState;
    } else if (routePrioritiesName && routePriorities && routePriorities.length > 0) {
      property = routePrioritiesName;
      value = routePriorities
    } else if (marketplaceOperatorEmails) {
      property = "marketplaceOperatorEmails";
      value = emailList;
    }
    else {
      property = e.currentTarget?.name ?? "";
      value = e.currentTarget?.value.trimStart() ?? "";
    }
    const updatedValues = operateSetting(property, number, value, type, channelID);
    const values: UpdatesValues = { property: property, value: value, type: type, channelId: channelID, number: number };
    let element = newValues.find((elem) => elem.property === values.property && elem.type === values.type && elem.channelId === channelID);
    newValues.push(values);
    if (element && newValues.length > 1) {
      const index = newValues.indexOf(element);
      newValues[index] = { property: element.property, type: element.type, value: values.value, channelId: channelID, number: number };
      newValues.pop();
      setNewValues([...newValues]);
    }
    if ((type === SettingTypes.Channel || type === SettingTypes.BalancePlatform) && channelID) {
      let channel = copyChannels.find((c) => c.id === channelID);
      if (channel) {
        const index = copyChannels.indexOf(channel);
        copyChannels[index] = updatedValues as ChannelConfig;
        setCopyChannels([...copyChannels]);
      }
    } else {
      setMarketplaceConfig(updatedValues);
    }
  };

  const assign = (obj: any, keyPath: string[], value: string | boolean | any[]) => {
    let copy = { ...obj };
    const lastKeyIndex = keyPath.length - 1;
    for (let i = 0; i < lastKeyIndex; ++i) {
      const key = keyPath[i];
      if (!(key in copy)) {
        copy[key] = {};
      }
      copy = copy[key];
    }
    copy[keyPath[lastKeyIndex]] = value;
    return copy;
  };

  const operateSetting = (property: string, number: boolean | undefined, value: string | boolean | string[], type?: SettingTypes, channelID?: number) => {
    const properties = property.split('.');
    const prev = { ...marketplaceConfig };
    const prevChannel = copyChannels.find((c) => c.id === channelID);
    switch (type) {
      case SettingTypes.Account: {
        let account: AccountConfig = { ...prev.account };
        account[property] = number ? +value : value;

        return {
          ...prev,
          account,
        };
      }
      case SettingTypes.Mirakl: {
        let mirakl: MiraklConfig = { ...prev.mirakl };
        mirakl[property] = number ? +value : value;
        return {
          ...prev,
          mirakl,
        };
      }

      case SettingTypes.Channel: {
        if (properties.length > 1) {
          assign(prevChannel, properties, value);
          const obj = {
            ...prevChannel,
          };
          return obj;
        } else {
          if (copyChannels.find((c) => c.code === value)) {
            displayError(`\nThe channel name is not unique`);
            setSaveButtonDisabled(true);
            return prev;
          } else {
            setSaveButtonDisabled(false);
            let channel: ChannelConfig = { ...prevChannel! };
            channel[property] = number ? +value : value;
            return { ...prevChannel, ...channel };
          }
        }
      }
      case SettingTypes.BalancePlatform: {
        if (properties.length > 1) {
          assign(prevChannel, properties, value);
          const obj = {
            ...prevChannel,
          };
          return obj;
        } else {
          let channel: ChannelConfig = { ...prevChannel! };
          channel[property] = number ? +value : value;
          return { ...prevChannel, ...channel };
        }
      }
      default:
        return prev;
    }
  };

  interface IChannelError {
    channelId: number;
    errors: [
      {
        message: string;
        [key: string]: string;
      },
    ];
  }

  const [channelErrors, setChannelErrors] = useState<IChannelError[]>([]);

  const registerErrors = () => {
    let result = true;
    const errors: IChannelError[] = [];
    for (let channel of copyChannels) {
      if (!channel) return false;

      if (!channel.code && channel.id) {
        displayError(`Channel name is required!`);
        const channelError: IChannelError = {
          channelId: channel.id,
          errors: [{ field: 'code', message: 'Channel name is required!' }],
        };
        errors.push(channelError);
        result = false;
      }
      if (channel.config.routePriorities?.length === 0 && channel.id) {
        displayError(`Channel payout priority is required!`);
        const channelError: IChannelError = {
          channelId: channel.id,
          errors: [{ field: 'code', message: 'Channel payout priority is required!' }],
        };
        errors.push(channelError);
        result = false;
      }
    }
    setChannelErrors(errors);
    return result;
  };

  const checkUsernameAndPassword = () => {
    let result = true;
    setChannelErrors([]);
    for (let channel of copyChannels) {
      if (!channel || !channel.notificationSettings) return false;

      result = registerErrors();
      if (!result) return result;

      const { configuration, legalEntity, transfer, transferInstrument } = channel.notificationSettings;
      result = checkInputs(configuration);

      if (!result) {
        displayError(`${channel.code} configuration username and password are required!`);
        return result;
      }
      result = checkInputs(legalEntity);
      if (!result) {
        displayError(`${channel.code} legal entity username and password are required!`);
        return result;
      }
      result = checkInputs(transfer);
      if (!result) {
        displayError(`${channel.code} transfer username and password are required!`);
        return result;
      }
      result = checkInputs(transferInstrument);
      if (!result) {
        displayError(`${channel.code} transfer instrument username and password are required!`);
        return result;
      }
    }

    return result;
  };

  function checkInputs(setting?: AdyenNotificationSecurity) {
    if (!setting) return true;
    if ((setting.user && !setting.password) || (setting.password && !setting.user)) {
      return false;
    }
    return true;
  }

  const clearIdsFromNewChannels = () => {
    const newChannels: ChannelConfig[] = copyChannels.map((channel) => {
      const { id, ...rest } = channel;
      const updated: ChannelConfig = {
        ...rest,
      };
      if (id && id > 0) {
        updated.id = id;
      }
      return updated;
    });
    return newChannels;
  };

  const validateBeforeSave = () => {
    let isCorrect = true;
    const errors = [''];

    if (!marketplaceName) {
      errors.push('- Marketplace name is required!\n');
      isCorrect = false;
    }
    if (!emailList || emailList.length === 0) {
      errors.push('- Operator emails are required!\n');
      isCorrect = false;
    }
    if (!URL_FORMAT.test(marketplaceConfig.account.hopReturnUrl)) {
      errors.push('- Enter a valid Hop return URL!\n');
      isCorrect = false;
    }
    if (!isCorrect && errors.length) {
      displayError(errors.join(''));
    }
    return isCorrect;
  };

  const updatedChannels = (element: any, obj: ChannelUpdateConfig) => {
    if (updatedValues.channels && updatedValues.channels.length > 0) {
      const sameId = updatedValues.channels.find((c: ChannelUpdateConfig) => c.id === element.channelId);
      if (sameId) {
        Object.entries(obj).forEach(([property, value]) => {
          if (property !== 'id') {
            sameId[property] = value;
          }
        });
      } else {
        const channelId = element.channelId;
        updatedValues.channels.push({ id: channelId, ...obj });
      }
    } else {
      obj.id = element.channelId;
      const channels: ChannelUpdateConfig[] = []
      if (updatedValues.channels) {
        channels.push(updatedValues.channels)
      }
      updatedValues = {
        ...updatedValues,
        channels: [...channels, obj]
      };

    }
  }

  const multiLevelProperty = (properties: string[], element: UpdatedValues, obj: ChannelUpdateConfig) => {
    // Use the reduce method to loop the properties array and build the object structure
    properties.reduce((object, propertie, index) => {
      // Check if it is the last property
      if (index === properties.length - 1) {
        // Return the value
        object[propertie] = element.value;
        return element.value;
      } else {
        // Verify if the property exists or create a new object to ensures the structure is maintained
        return object[propertie] = object[propertie] || {};
      }
    }, obj);
  }

  const updateMarketplaceValues = () => {
    let obj: ChannelUpdateConfig = {};
    newValues.forEach((element) => {
      const properties = element.property.split('.');
      switch (element.type) {
        case SettingTypes.Account: {
          let accountObj;
          if (element.property !== "") {
            accountObj = { [element.property]: element.number ? +element.value : element.value };
          }
          if (updatedValues.config && updatedValues.config.account) {
            updatedValues.config.account = { ...updatedValues.config.account, ...accountObj }
          } else {
            updatedValues = {
              ...updatedValues, config: { ...updatedValues.config, account: accountObj },
            };
          }
          break;
        }
        case SettingTypes.Mirakl: {
          let miraklObj = { [element.property]: element.number ? +element.value : element.value };
          if (updatedValues.config && updatedValues.config.mirakl) {
            updatedValues.config.mirakl = { ...updatedValues.config.mirakl, ...miraklObj }
          } else {
            updatedValues = {
              ...updatedValues, config: { ...updatedValues.config, mirakl: miraklObj },
            };
          }
          break;
        }
        case SettingTypes.Channel: {
          if (properties.length > 1) {
            multiLevelProperty(properties, element, obj)
            updatedChannels(element, obj);
          } else if (properties[0] !== "") {
            let channelObj = { [element.property]: element.number ? +element.value : element.value, id: element.channelId };
            updatedChannels(element, channelObj);
          }
          break;
        }
        case SettingTypes.BalancePlatform: {
          if (properties.length > 1) {
            multiLevelProperty(properties, element, obj)
            updatedChannels(element, obj);
          } else if (properties[0] !== "") {
            let channelObj = { [element.property]: element.number ? +element.value : (element.value === '' ? '' : element.value), id: element.channelId };
            updatedChannels(element, channelObj);
          }
          break;
        }
      }
    });
    if (invoiceBlacklist.length > 0 || shopBlacklist.length > 0) {
      updatedValues = {
        ...updatedValues, config: {
          ...updatedValues.config, blacklists: {
            shops: shopBlacklist || [],
            invoices: invoiceBlacklist || []
          },
        },
      };
    }
    updatedValues = {
      ...updatedValues,
      id: marketplaceId,
      name: marketplaceName,
      disabled: disabledCreatedMarketplace,
    };
  }


  const manageHealthStateIndicator = (
    miraklListOperatorPermissions: MiraklPlatformConfigurations
  ): { status: 0 | 1 | 2; messages: string[] } => {
    const features = miraklListOperatorPermissions?.features;
    const kycStatus = features?.kycStatusPerSeller;
    const paymentConfirmation = features?.paymentConfirmation;
    const taxIdMandatory = features?.shopTaxOptions?.taxIdentificationNumber?.mandatory;
    const localTaxMandatory = features?.shopTaxOptions?.localTaxNumber?.mandatory;

    let messages: string[] = [];

    if (!kycStatus || !paymentConfirmation) {
      let missingFields = [];
      if (!kycStatus) missingFields.push("kycStatusPerSeller");
      if (!paymentConfirmation) missingFields.push("paymentConfirmation");
      const msg = `The following Mirakl operator permissions are missing or disabled: ${missingFields.join(", ")}. Please enable them for correct operation.`;
      messages.push(msg);
      return { status: 0, messages };
    }

    if (kycStatus && paymentConfirmation && (taxIdMandatory || localTaxMandatory)) {
      let whichTrue = [];
      if (taxIdMandatory) whichTrue.push("tax_identification_number");
      if (localTaxMandatory) whichTrue.push("local_tax_number");
      const isOrAre = whichTrue.length === 1 ? "is" : "are";
      const msg = `${whichTrue.join(" and ")} ${isOrAre} mandatory - This may cause issues with sellers that do not have a local tax number e.g. sole traders.`;
      messages.push(msg);
      return { status: 1, messages };
    }

    if (kycStatus && paymentConfirmation && !taxIdMandatory && !localTaxMandatory) {
      return { status: 2, messages };
    }

    return { status: 0, messages };
  };

  const checkBalancePlarformName = () => {
    let isUnique = true;
    const balancePlatformCount = copyChannels.reduce((acc, channel) => {
      if (channel.balancePlatform) {
        acc[channel.balancePlatform] = (acc[channel.balancePlatform] || 0) + 1;
      }
      return acc;
    }, {} as { [key: string]: number });

    Object.keys(balancePlatformCount).forEach(platform => {
      if (balancePlatformCount[platform] > 1) {
        displayError(`\nThe Balance Platform name is not unique`);
        setSaveButtonDisabled(true);
        isUnique = false
      }
    });
    return isUnique
  }

  const handleCreateMarketplace = async () => {
    try {
      setSaveButtonDisabled(true);
      updateMarketplaceValues();
      if (!checkUsernameAndPassword()) {
        setSaveButtonDisabled(false);
        return;
      }
      if (!checkBalancePlarformName()) {
        setSaveButtonDisabled(false);
        return;
      }

      const emailList = createEmails();
      const channels = clearIdsFromNewChannels();
      if (!validateBeforeSave()) {
        setSaveButtonDisabled(false);
        return;
      }
      if (marketplaceId) {
        if (!isMinimumHOPQuestionsValid(updatedValues.config.account.noOfSellerQuestions)) {
          return;
        }
        const cleanedNegativeChannelsId = updatedValues.channels?.map((channel: Channel) => {
          const channelId = channel.id;
          if (channelId && channelId < 0) {
            const repeatChannel = copyChannels.find((c) => c.code === channel.code && c.id !== channel.id);
            if (!repeatChannel) {
              const newChannel = copyChannels.find((c) => c.id === channelId);
              if (newChannel) {
                const { id, ...rest } = newChannel;
                return rest;
              }
            } else {
              return undefined;
            }
          }
          return channel;
        }).filter((channel: Channel) => channel !== undefined);
        updatedValues.channels = cleanedNegativeChannelsId;
        if (updatedValues.config && updatedValues.config.account && updatedValues.config.account.marketplaceOperatorEmails) {
          updatedValues['config']['account']['marketplaceOperatorEmails'] = emailList;
        }
        await updateMarketplace({
          variables: {
            data: updatedValues as UpdateMarketplaceInput
          },
        });
      } else {
        if (!isMinimumHOPQuestionsValid(marketplaceConfig.account.noOfSellerQuestions)) {
          return;
        }
        console.log(marketplaceConfig.account.noOfSellerQuestions)
        await createMarketplace({
          variables: {
            data: {
              name: marketplaceName,
              disabled: disabledCreatedMarketplace,
              config: {
                ...marketplaceConfig,
                account: {
                  ...marketplaceConfig.account,
                  marketplaceOperatorEmails: emailList,
                  useChannels,
                  useBalancePlatforms
                },
                blacklists: {
                  shops: shopBlacklist || [],
                  invoices: invoiceBlacklist || []
                },
              },
              channels,
            },
          },
        });
      }
    } catch (error) {
      displayError(error as string);
    } finally {
      setSaveButtonDisabled(false);
    }
  };

  const checkMinimumOfHOPQuestions = () => {
    let minimumHOPQuestions = 0;
    if (
      process.env.REACT_APP_BACKEND_SERVER &&
      (process.env.REACT_APP_BACKEND_SERVER.includes("localhost") ||
        process.env.REACT_APP_BACKEND_SERVER.includes("dev") ||
        process.env.REACT_APP_BACKEND_SERVER.includes("stg"))
    ) {
      minimumHOPQuestions = 1;
    } else {
      minimumHOPQuestions = 3;
    }
    return minimumHOPQuestions;
  };

  const isMinimumHOPQuestionsValid = (noOfSellerQuestions: number) => {
    const min = checkMinimumOfHOPQuestions();
    if (noOfSellerQuestions < min) {
      displayError("Minimum number of HOP questions is " + min);
      return false;
    }
    return true;
  };

  const handleCheckMarketplaceConfig = () => {
    const marketplaceOperatorEmails = createEmails();
    if (!isMinimumHOPQuestionsValid(marketplaceConfig.account.noOfSellerQuestions)) {
      return;
    }
    setValidateButtonDisabled(true);
    checkMarketplaceConfig({
      variables: {
        config: {
          ...marketplaceConfig,
          account: {
            ...marketplaceConfig.account,
            marketplaceOperatorEmails,
          },
          blacklists: {
            shops: [],
            invoices: []
          },
        },
        channels: copyChannels,
        marketplaceId: +marketplaceId!,
      },
    });
  };

  const generateRandomNumber = () => {
    return Math.floor(Math.random() * -1000);
  };

  const generateDefaultChannel = () => {
    const newChannel: ChannelConfig = {
      id: 1,
      code: "Adyen",
      isDefault: true,
      balancePlatform: "",
      balanceAccountId: "",
      balanceApi: {
        isBasicAuth: false,
        password: '',
        url: '',
        user: '',
        key: '',
      },
      lemApi: {
        isBasicAuth: false,
        password: '',
        url: '',
        user: '',
        key: '',
      },
      notificationUrl: '',
      notificationSettings: {
        configuration: { hmac: '', password: '', user: '' },
        transfer: { hmac: '', password: '', user: '' },
        transferInstrument: { hmac: '', password: '', user: '' },
        legalEntity: { hmac: '', password: '', user: '' },
      },
      config: {
        hopOptions: {
          allowBankAccountFormatSelection: true,
          allowIntraRegionCrossBorderPayout: false,
        },
        routePriorities: 'regular'
      }
    };
    setCopyChannels([newChannel]);
  };

  const handleToggleChange = (channelId: number | undefined, option: string) => {
    setCopyChannels((prevChannels) => {
      return prevChannels.map((channel) => {
        if (channel.id === channelId) {
          return {
            ...channel,
            config: {
              ...channel.config,
              hopOptions: {
                ...(channel.config.hopOptions || {
                  allowBankAccountFormatSelection: true,
                  allowIntraRegionCrossBorderPayout: false
                }),
                [option]: !channel.config?.hopOptions?.[option],
              }
            }
          };
        }
        return channel;
      });
    });
  };

  const handleAddChannel = () => {
    if (channelName || balancePlatformName) {
      if (copyChannels.filter((t) => t.code === channelName).length > 0) {
        setIsChannelModalOpened(false);
        displayError(`\nThe channel name is not unique`);
      } else {
        if (channelName) {
          const channelId = generateRandomNumber();
          const newChannel: ChannelConfig = {
            id: channelId,
            code: channelName,
            isDefault: false,
            balancePlatform: "",
            balanceAccountId: "",
            balanceApi: {
              isBasicAuth: false,
              password: '',
              url: '',
              user: '',
              key: '',
            },
            lemApi: {
              isBasicAuth: false,
              password: '',
              url: '',
              user: '',
              key: '',
            },
            notificationUrl: '',
            notificationSettings: {
              configuration: { hmac: '', password: '', user: '' },
              transfer: { hmac: '', password: '', user: '' },
              transferInstrument: { hmac: '', password: '', user: '' },
              legalEntity: { hmac: '', password: '', user: '' },
            },
            config: {
              hopOptions: {
                allowBankAccountFormatSelection: true,
                allowIntraRegionCrossBorderPayout: false
              },
              routePriorities: 'regular'
            }
          };
          setCopyChannels([...copyChannels, newChannel]);
          setIsChannelModalOpened(false);
          setChannelName("");
          const codeUpdate = {
            property: "code",
            type: SettingTypes.Channel,
            value: channelName,
            channelId: channelId,
            number: false
          };
          setNewValues([...newValues, codeUpdate]);
        } else {
          const balancePlatformId = generateRandomNumber();
          const newBalancePlatform: ChannelConfig = {
            id: balancePlatformId,
            code: balancePlatformName,
            isDefault: false,
            balancePlatform: balancePlatformName,
            balanceAccountId: "",
            balanceApi: {
              isBasicAuth: false,
              password: '',
              url: '',
              user: '',
              key: '',
            },
            lemApi: {
              isBasicAuth: false,
              password: '',
              url: '',
              user: '',
              key: '',
            },
            notificationUrl: '',
            notificationSettings: {
              configuration: { hmac: '', password: '', user: '' },
              transfer: { hmac: '', password: '', user: '' },
              transferInstrument: { hmac: '', password: '', user: '' },
              legalEntity: { hmac: '', password: '', user: '' },
            },
            config: {
              hopOptions: {
                allowBankAccountFormatSelection: true,
                allowIntraRegionCrossBorderPayout: false
              },
              routePriorities: 'regular'
            }
          };
          setCopyChannels([...copyChannels, newBalancePlatform]);
          setIsBalancePlatformModalOpened(false);
          setBalancePlatformName("");
          const codeUpdate = {
            property: "code",
            type: SettingTypes.BalancePlatform,
            value: balancePlatformName,
            channelId: balancePlatformId,
            number: false
          };
          setNewValues([...newValues, codeUpdate]);
        }
      }
    }
  };

  // const checkTabTilte = (channel: any) => {
  //   if (!useChannels) {
  //     if (useBalancePlatform) {
  //       return `Balance Platform: ${channel.code}`
  //     } else {
  //       return channel.code
  //     }
  //   } else {
  //     return `Channel: ${channel.code}`
  //   }
  // }



  return (
    <div className="main-content__result">
      <Tabs forceRenderTabPanel defaultIndex={0}>
        <TabList className="tabs__list">
          <Tab>Account</Tab>
          <Tab>Mirakl</Tab>
          {copyChannels
            // .sort((a, b) => a.code.localeCompare(b.code))
            .map((channel, index) => {
              const hasErrors = channelErrors.find((error) => error.channelId === channel.id);
              return (
                <Tab style={{ border: hasErrors ? '1px solid red' : '' }} key={index}>
                  {!useChannels ? channel.code : `Channel: ${channel.code}`}
                  {/* {checkTabTilte(channel)} */}
                  {hasErrors && (
                    <span
                      style={{
                        color: 'red',
                        position: 'absolute',
                        bottom: channel.code ? 30 : 20,
                        right: 0,
                        zIndex: 9999,
                      }}
                    >
                      <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                    </span>
                  )}
                </Tab>
              );
            })}
          {useChannels && (
            <li className="react-tabs__tab big-plus-button" onClick={() => setIsChannelModalOpened(true)}>
              +
            </li>
          )}
          {useBalancePlatforms && (
            <li className="react-tabs__tab big-plus-button" onClick={() => setIsBalancePlatformModalOpened(true)}>
              +
            </li>
          )}
        </TabList>
        <TabPanel>
          <AccountSettings
            marketplaceConfig={marketplaceConfig}
            marketplaceName={marketplaceName}
            emailList={emailList}
            setMarketplaceConfig={setMarketplaceConfig}
            setMarketplaceName={setMarketplaceName}
            setEmailList={setEmailList}
            handleStateUpdate={handleStateUpdate}
            setSaveButtonDisabled={setSaveButtonDisabled}
            useChannels={useChannels}
            setUseChannels={setUseChannels}
            useBalancePlatforms={useBalancePlatforms}
            setUseBalancePlatforms={setUseBalancePlatforms}
            copyChannels={copyChannels}
            setCopyChannels={setCopyChannels}
            marketplaceId={marketplaceId} />
        </TabPanel>
        <TabPanel>
          <MiraklSettings marketplaceConfig={marketplaceConfig} handleStateUpdate={handleStateUpdate} />
        </TabPanel>
        {copyChannels
          // .sort((a, b) => a.code.localeCompare(b.code))
          .map((channel, index) => (
            <TabPanel key={index}>
              <ChannelSettings
                handleStateUpdate={handleStateUpdate}
                useChannels={useChannels}
                channel={channel}
                channelErrors={channelErrors}
                channels={copyChannels}
                marketplaceId={marketplaceId}
                handleToggleChange={handleToggleChange}
                setSaveButtonDisabled={setSaveButtonDisabled}
              />
            </TabPanel>
          ))}
      </Tabs>
      <div className='validate-container'>
        <div className="radio-validate-container">
          <div />
          <Button
            content="Validate"
            type="button"
            className="button--bordered button--green"
            icon=""
            onClick={handleCheckMarketplaceConfig}
            disabled={validateButtonDisabled}
          />
        </div>
        <HealthStateIndicator healthIndicatorState={healthIndicatorState} />
      </div>
      <div className="buttons-wrapper">
        {marketplaceId ? (
          <Button
            className="button--bordered button--grey"
            type="button"
            content="View Logs"
            icon="logs"
            onClick={() =>
              navigate({
                pathname: getRoutePath(AdyenRoutesName.MARKETPLACE_LOGS),
                search: `${createSearchParams({
                  marketplaceId: marketplaceId.toString(),
                  name: marketplaceName,
                })}`,
              })
            }
          ></Button>
        ) : (
          <div />
        )}
        <div className="left-buttons-container">
          <Button className="button--bordered button--error" type="button" content="Cancel" icon="" onClick={() => navigate(-1)}></Button>
          <Button
            className="button--primary"
            type="button"
            content="Save"
            icon=""
            onClick={handleCreateMarketplace}
            disabled={saveButtonDisabled}
          ></Button>
        </div>
      </div>
      <Modal title="Add New Channel" isOpened={isChannelModalOpened} setIsOpenedCallback={setIsChannelModalOpened}>
        <div className="channel-modal">
          <InputField
            label="Channel Name *"
            placeholder="Enter Channel Name"
            name="channelName"
            onChange={(e) => setChannelName(e.target.value)}
            value={channelName}
          />
          <div className="buttons-wrapper">
            <Button
              className="button--bordered button--grey"
              type="button"
              content="Cancel"
              icon=""
              onClick={() => setIsChannelModalOpened(false)}
            ></Button>
            <Button className="button--primary" type="button" content="Add" icon="" onClick={handleAddChannel}></Button>
          </div>
        </div>
      </Modal>
      <Modal title="Add New Balance Platform" isOpened={isBalancePlatformModalOpened} setIsOpenedCallback={setIsBalancePlatformModalOpened}>
        <div className="channel-modal">
          <InputField
            label="Balance Platform Name *"
            placeholder="Enter Balance Platform Name"
            name="channelName"
            onChange={(e) => setBalancePlatformName(e.target.value)}
            value={balancePlatformName}
          />
          <div className="buttons-wrapper">
            <Button
              className="button--bordered button--grey"
              type="button"
              content="Cancel"
              icon=""
              onClick={() => setIsBalancePlatformModalOpened(false)}
            ></Button>
            <Button className="button--primary" type="button" content="Add" icon="" onClick={handleAddChannel}></Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
export default FirstMarketplaceForm;
